import * as Sentry from '@sentry/react';

import { isRunningLocally } from '@/common/infrastructure/config/env';

export interface Logger {
  warn(message: string, ...args: unknown[]): void;
  error(message: string, error?: unknown): void;
  info(message: string, ...args: unknown[]): void;
  debug(message: string, ...args: unknown[]): void;
}

export class LoggerService implements Logger {
  private readonly context: string;

  constructor(context: string = 'Application') {
    this.context = context;
  }

  public warn(message: string, ...args: unknown[]): void {
    const formatted = `[${this.context}] ${message}`;
    console.warn(formatted, ...args);

    if (!isRunningLocally()) {
      Sentry.addBreadcrumb({
        category: 'warning',
        message: formatted,
        data: args.length > 0 ? { args } : undefined,
        level: 'warning'
      });
    }
  }

  public error(message: string, error?: unknown): void {
    const formatted = `[${this.context}] ${message}`;
    console.error(formatted, error);

    if (!isRunningLocally()) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          tags: { context: this.context },
          extra: { message: formatted }
        });
      } else {
        Sentry.captureMessage(formatted, {
          level: 'error',
          tags: { context: this.context },
          extra: { error }
        });
      }
    }
  }

  public info(message: string, ...args: unknown[]): void {
    const formatted = `[${this.context}] ${message}`;
    console.info(formatted, ...args);

    if (!isRunningLocally()) {
      Sentry.addBreadcrumb({
        category: 'info',
        message: formatted,
        data: args.length > 0 ? { args } : undefined,
        level: 'info'
      });
    }
  }

  public debug(message: string, ...args: unknown[]): void {
    if (import.meta.env.DEV) {
      console.debug(`[${this.context}] ${message}`, ...args);
    }
  }

  public static create(context: string): Logger {
    return new LoggerService(context);
  }
}

export const defaultLogger: Logger = new LoggerService();

export default defaultLogger;
